import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { CircularProgress, Typography, Box, Button } from '@mui/material';
import useAuth from 'src/hooks/useAuth';
import { useTranslation } from 'react-i18next';

const params = new URLSearchParams(window.location.search);

const RegisterVerification: FC = () => {
  const { userVerification, logout } = useAuth();
  const { t } = useTranslation();
  const token = params.get('token');
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verificationAttempted, setVerificationAttempted] = useState(false);

  useEffect(() => {
    // Prevent multiple verification attempts
    if (verificationAttempted) return;
    setVerificationAttempted(true);

    const userVerificationAuth = async () => {
      if (!token) {
        console.log('No token provided, redirecting to company registration');
        setIsLoading(false);
        // Force direct page load to refresh state completely
        window.location.href = '/company-registration';
        return;
      }

      try {
        console.log('Verifying with token:', token);
        await userVerification(token);

        // Force direct page load to refresh state completely
        window.location.href = '/company-registration';
      } catch (error) {
        console.error('Verification failed:', error);
        setError(t('EMAIL_VERIFICATION_FAILED'));
        setIsLoading(false);
      }
    };

    userVerificationAuth();
  }, [token, navigate, userVerification, verificationAttempted, t]);

  // Force a timeout to ensure we don't get stuck loading
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isLoading) {
        setIsLoading(false);
        setError(t('EMAIL_VERIFICATION_TIMEOUT'));
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timer);
  }, [isLoading, t]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
      >
        <CircularProgress />
        <Typography variant="body1" style={{ marginTop: 16 }}>
          {t('VERIFYING_EMAIL')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
      >
        <Typography variant="h6" color="error">
          {error}
        </Typography>
        <Box mt={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => (window.location.href = '/login')}
          >
            {t('GO_TO_LOGIN')}
          </Button>
        </Box>
      </Box>
    );
  }

  // Force redirect if we somehow get here
  window.location.href = '/company-registration';
  return null;
};

export default RegisterVerification;
